import React from 'react';
import { formatFriendlyTime, capitalizeFirstLetter, getCategoryClass } from '../utils/helpers';

const LoadingSpinner = () => (
  <li className="loading-container">
    <div className="loading-spinner">
      <svg viewBox="0 0 50 50" className="spinner">
        <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
      </svg>
    </div>
  </li>
);

const ErrorMessage = ({ error, onRetry }) => (
  <li className="error-container">
    <div className="error-icon">⚠️</div>
    <h2 className="error-title">Kunde inte ladda artiklar</h2>
    <p className="error-message">
      {error === 'AbortError' 
        ? 'Servern svarar inte. Försök igen om en stund.'
        : 'Ett fel uppstod när artiklarna skulle hämtas.'}
    </p>
    <button onClick={onRetry} className="retry-button">
      🔄 Försök igen
    </button>
  </li>
);

const NoArticles = ({ selectedRegions, excludedCategories }) => {
  const hasFilters = (selectedRegions && selectedRegions.length > 0) || 
                     (excludedCategories && excludedCategories.length > 0);
  
  return (
    <li className="error-container">
      <h2 className="error-title">Inga artiklar hittades</h2>
      <p className="error-message">Det finns inga artiklar att visa just nu.</p>
      
      {hasFilters && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          background: 'rgba(255, 165, 0, 0.1)',
          border: '1px solid rgba(255, 165, 0, 0.3)',
          borderRadius: '8px',
          fontSize: '13px',
          textAlign: 'left',
          maxWidth: '400px',
          margin: '20px auto 0'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>🔍 Debug-information:</div>
          
          {selectedRegions && selectedRegions.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <strong>Valda regioner ({selectedRegions.length}):</strong>
              <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.9 }}>
                {selectedRegions.join(', ')}
              </div>
            </div>
          )}
          
          {excludedCategories && excludedCategories.length > 0 && (
            <div>
              <strong>Exkluderade kategorier ({excludedCategories.length}):</strong>
              <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.9 }}>
                {excludedCategories.join(', ')}
              </div>
            </div>
          )}
          
          <div style={{ marginTop: '12px', fontSize: '12px', opacity: 0.8 }}>
            💡 Prova att justera dina filter om du inte får några resultat
          </div>
        </div>
      )}
    </li>
  );
};

const ArticleItem = ({ article, isRead, isNew, onClick }) => {
  const categoryClass = getCategoryClass(article.category);
  const imagePosition = article.image_url ? article.id % 2 === 0 ? 'image-right' : 'image-left' : '';
  
  return (
    <li 
      className={`article-card ${isRead ? 'read' : ''} ${isNew ? 'new-article' : ''} ${imagePosition}`}
      onClick={() => onClick(article)}
    >
      <div className="article-card-content">
        {/* Region in top left */}
        {article.region && article.region.trim() && (
          <div className="region-tag">
            {article.region.toUpperCase()}
          </div>
        )}
        
        {/* Category in top right */}
        <div className={`category-tag ${categoryClass}`}>
          {capitalizeFirstLetter(article.category).toUpperCase()}
        </div>
        
        {/* Image */}
        {article.image_url && (
          <div className="article-image-container">
            <img 
              className="article-image"
              src={article.image_url} 
              alt={article.image_alt || article.swedish_title || article.original_title}
              loading="lazy"
            />
          </div>
        )}
        
        {/* Main content area */}
        <div className="article-text-content">
          <h2 className="article-title">
            {article.swedish_title || article.original_title}
          </h2>
          <div className="article-summary">
            {article.short_summary}
          </div>
        </div>
        
        
        {/* Time in bottom right */}
        <div className="article-time">
          {formatFriendlyTime(article.inserted_datetime)}
        </div>
      </div>
    </li>
  );
};

const ArticleList = ({ 
  articles, 
  readArticles, 
  newArticleIds = [],
  isLoading, 
  error, 
  onArticleClick, 
  onRetry,
  selectedRegions,
  excludedCategories
}) => {
  // Show error if there's an error and no articles
  if (error && articles.length === 0) {
    return (
      <ul className="articles-container">
        <ErrorMessage error={error} onRetry={onRetry} />
      </ul>
    );
  }

  // Show loading spinner if loading and no articles
  if (isLoading && articles.length === 0) {
    return (
      <ul className="articles-container">
        <LoadingSpinner />
      </ul>
    );
  }

  // Show no articles message if not loading and no articles
  if (!isLoading && articles.length === 0) {
    return (
      <ul className="articles-container">
        <NoArticles 
          selectedRegions={selectedRegions}
          excludedCategories={excludedCategories}
        />
      </ul>
    );
  }

  // Render articles list
  return (
    <ul className="articles-container">
      {articles.map((article, index) => (
        <ArticleItem
          key={`${article.id}-${index}`}
          article={article}
          isRead={readArticles.includes(article.id)}
          isNew={newArticleIds.includes(article.id)}
          onClick={onArticleClick}
        />
      ))}
      {isLoading && <LoadingSpinner />}
    </ul>
  );
};

export default ArticleList;
